import { Box } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { RwIconButton } from '@/common/presentation/components/rw';
import { LibraryOverflowMenu, type LibraryOverflowAction } from './LibraryOverflowMenu';

export const LIBRARY_CARD_OVERFLOW_SLOT = 'library-card-overflow';
export const LIBRARY_CARD_OPEN_SLOT = 'library-card-open';

const hiddenUntilRevealed = {
  position: 'absolute',
  zIndex: 2,
  opacity: 0,
  pointerEvents: 'none',
  transition: 'opacity var(--duration-medium, 200ms) ease',
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
} as const;

export interface LibraryCardHoverControlsProps {
  openLabel: string;
  onOpen: () => void;
  actions: LibraryOverflowAction[];
  actionsLabel: string;
  actionsTestId?: string;
  openTestId?: string;
}

/**
 * The two controls layered over a library card's preview well, revealed by the card's own
 * `:hover`/`:focus-within` rule (keyed on the `data-slot` values above): a ghost ⋮ overflow at the
 * top-right and a primary square arrow at the bottom-right that opens the record (pointer and touch
 * only — keyboard and screen-reader users open the card through its link).
 */
export function LibraryCardHoverControls({
  openLabel,
  onOpen,
  actions,
  actionsLabel,
  actionsTestId,
  openTestId
}: LibraryCardHoverControlsProps) {
  return (
    <>
      {actions.length > 0 ? (
        <Box data-slot={LIBRARY_CARD_OVERFLOW_SLOT} sx={{ ...hiddenUntilRevealed, top: 'var(--size-16)', right: 'var(--size-16)' }}>
          <LibraryOverflowMenu actions={actions} label={actionsLabel} testId={actionsTestId} size="sm" tier="ghost" />
        </Box>
      ) : null}
      <Box data-slot={LIBRARY_CARD_OPEN_SLOT} sx={{ ...hiddenUntilRevealed, bottom: 'var(--size-16)', right: 'var(--size-16)' }}>
        {/* A pointer-only duplicate of the full-card open link: skipped by Tab and hidden from assistive
            tech so the card has one open stop, and no second control carrying the same name. */}
        <RwIconButton tier="primary" size="lg" aria-label={openLabel} aria-hidden tabIndex={-1} data-testid={openTestId} onClick={onOpen}>
          <ArrowForwardIcon aria-hidden sx={{ fontSize: 'var(--size-20)' }} />
        </RwIconButton>
      </Box>
    </>
  );
}
